/**
 * @function isNumber
 * @param {n} n
 * @returns {boolean}
 */
export const isNumber = (v: unknown): v is number => typeof v === 'number' && !isNaN(v);

/**
 * Return a number Value as a String with commas
 *
 * @param {Number} value - number as string
 * @returns String
 */
export const numberWithCommas = (value: string | number): string => {
  return (value ?? '').toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Determines if the Number as a String has any leading zeroes
 *
 * @param {String} number - as a string
 * @returns Boolean
 */
export const hasLeadingZeroes = (number: string): boolean => {
  return typeof number === 'string' && number.length > 1 && number[0] === '0' && number[1] !== '.';
};

/**
 * Returns a String value with no commas
 *
 * @param {String} value - value as a string with commas
 * @returns String
 */
export const stripCommas = (value: string | number): string => {
  return (value ?? '').toString().replace(',', '');
};

/**
 * Ensures that the number Value as a String comes back as a
 * clean Number as a String
 *  - no leading zeroes
 *  - adds a Zero in front a decimal if it's the only character
 *
 * @param {String} value - number as a string
 * @returns String
 */
export const numberOnly = (value: string): string => {
  let number = (value ?? '').replace(/[^0-9.]/g, '') || '0';

  if (hasLeadingZeroes(number)) {
    number = number.slice(1);
  }
  if (number === '.') {
    number = '0.';
  }
  return number;
};

/**
 * Removes all non-numeric values from a string.
 * @param {String} value - number as a string
 * @returns String
 */
export const removeNaN = (value: string): string => {
  return (value ?? '').replace(/[^0-9.]/g, '');
};

/**
 * Convenience method to convert value as a Number or String and
 * return a Float with fixed decimals (default 2)
 *
 * @param {string | number} value - number as a string
 * @param {number} decimals - default 2
 * @returns string
 */
export const toNumber = (value: string | number, decimals = 2): string => {
  return parseFloat(stripCommas(value)).toFixed(decimals);
};

/**
 * Convenience method to convert value as a Number or a String and
 * return a number as a String with commas
 *
 * @param {String | Number} value - number value
 * @param {Number} decimals - default 0
 * @returns String
 */
export const toPoints = (value: string | number, decimals = 0): string => {
  return numberWithCommas(toNumber(value, decimals));
};

/**
 * Convenience method to convert value as a Number or a String and
 * return a number as a String with commas
 *
 * @param {String | Number} value - number value
 * @param {Number} decimals - default 2
 * @returns String
 */
export const toCurrency = (value: string | number, decimals = 2): string => {
  return numberWithCommas(toNumber(value, decimals));
};

/**
 * Convenience method to convert Currency to Points
 *
 * @param {String | Number} currencyValue
 * @param {Number} conversionFactor
 * @returns Number
 */
export const convertToPoints = (
  currencyValue: string | number,
  conversionFactor: number
): number => {
  return parseFloat(stripCommas(currencyValue)) / conversionFactor;
};

/**
 * Convenience method to convert Points to Currency
 *
 * @param {String | Number} pointsValue - no commas allowed
 * @param {Number} conversionFactor
 * @returns Number
 */
export const convertToCurrency = (
  pointsValue: string | number,
  conversionFactor: number
): number => {
  return parseFloat(stripCommas(pointsValue)) * conversionFactor;
};
